import axios from "axios";
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "react-query";
import { API_CACHE_KEYS, ApiRoutes } from "../constants/apiRoutes";
import { IOrganization, IQuote, IQuoteLineItem, PromotionCatalogQuery, PromotionCatalogResult, QuoteCopyTarget, QuoteCreateRequest, QuoteEmailProfile, QuoteEmailRequest, QuotePricePreviewRequest, QuotePromotionsRequest, QuotePromotionsResult, QuoteTotalsLine, QuoteTotalsRequest, QuoteTotalsResult, QuoteTotalsSection, QuoteUpdateRequest } from "../shared/types";
import { resolveDynamicUrl } from "../shared/utils";
import { CUSTOMER_CACHE_REFRESH_INTERVAL_MS, isCustomerSyncWindowActive } from "../shared/customerSync";

export const useGetQuotes = (
  includeArchived: boolean,
  options?: UseQueryOptions<IQuote[], unknown, IQuote[]>
) =>
  useQuery<IQuote[], unknown, IQuote[]>(
    [API_CACHE_KEYS.QUOTES, includeArchived],
    async () => {
      const { data } = await axios.get(ApiRoutes.quotes, {
        params: includeArchived ? { includeArchived: true } : undefined,
      });
      return data;
    },
    { ...options, placeholderData: [], retry: 0 }
  );

export const useGetQuoteCustomers = () =>
  useQuery<IOrganization[], unknown, IOrganization[]>(
    [API_CACHE_KEYS.QUOTES, "customers"],
    async () => (await axios.get(ApiRoutes.quoteCustomers)).data,
    {
      placeholderData: [],
      retry: 0,
      staleTime: 0,
      refetchInterval: () => isCustomerSyncWindowActive() ? CUSTOMER_CACHE_REFRESH_INTERVAL_MS : false,
    }
  );

// Issue 31: the distinct, normalized affiliate names already used on quotes, for the
// affiliate-name autocomplete on the create / edit screens.
export const useGetQuoteAffiliates = () =>
  useQuery<string[], unknown, string[]>(
    [API_CACHE_KEYS.QUOTES, "affiliates"],
    async () => (await axios.get(ApiRoutes.quoteAffiliates)).data,
    { placeholderData: [], retry: 0 }
  );

export const useGetQuote = (quoteId: string) =>
  useQuery<IQuote, null, IQuote>(
    [API_CACHE_KEYS.QUOTES, quoteId],
    async () => (await axios.get(resolveDynamicUrl(ApiRoutes.quote, { quoteId }))).data,
    { retry: 0 }
  );

export const useCopyQuote = (quoteId: string) => {
  const queryClient = useQueryClient();
  return useMutation<IQuote, unknown, QuoteCopyTarget>(
    async (target) => {
      const url = resolveDynamicUrl(ApiRoutes.copyQuote, { quoteId });
      return (await (Object.keys(target).length ? axios.post(url, target) : axios.post(url))).data;
    },
    { onSuccess: () => queryClient.invalidateQueries(API_CACHE_KEYS.QUOTES) }
  );
};

export const useCreateQuote = () => {
  const queryClient = useQueryClient();
  return useMutation<IQuote, unknown, QuoteCreateRequest>(
    async (request) => (await axios.post(ApiRoutes.quotes, request)).data,
    { onSuccess: () => queryClient.invalidateQueries(API_CACHE_KEYS.QUOTES) }
  );
};

export const useUpdateQuote = (quoteId: string) => {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, QuoteUpdateRequest>(
    async (request) => { await axios.put(resolveDynamicUrl(ApiRoutes.quote, { quoteId }), request); },
    { onSuccess: () => {
      queryClient.invalidateQueries([API_CACHE_KEYS.QUOTES, quoteId]);
      queryClient.invalidateQueries(API_CACHE_KEYS.QUOTES);
    } }
  );
};

// Issue 46: delete a quote that has never been finalized (soft delete server-side). Removes the
// quote from every cached list and drops its individual query cache entry - a subsequent GET
// would 404 anyway, the same way a hard-refreshed list already excludes it.
export const useDeleteQuote = () => {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>(
    async (quoteId) => { await axios.delete(resolveDynamicUrl(ApiRoutes.quote, { quoteId })); },
    { onSuccess: (_data, quoteId) => {
      queryClient.removeQueries([API_CACHE_KEYS.QUOTES, quoteId]);
      queryClient.invalidateQueries(API_CACHE_KEYS.QUOTES);
    } }
  );
};

export const useRefreshQuote = (quoteId: string) => {
  const queryClient = useQueryClient();
  return useMutation<IQuote, unknown, void>(
    async () => (await axios.post(resolveDynamicUrl(ApiRoutes.refreshQuote, { quoteId }))).data,
    { onSuccess: refreshed => {
      queryClient.setQueryData([API_CACHE_KEYS.QUOTES, String(refreshed.quote_id)], refreshed);
      queryClient.invalidateQueries([API_CACHE_KEYS.QUOTES, quoteId]);
      queryClient.invalidateQueries(API_CACHE_KEYS.QUOTES);
    } }
  );
};

const useQuoteStateMutation = (quoteId: string, route: string) => {
  const queryClient = useQueryClient();
  return useMutation<IQuote, unknown, { void_reason: string } | void>(
    async (request) => (await axios.post(resolveDynamicUrl(route, { quoteId }), request)).data,
    { onSuccess: (quote) => {
      queryClient.setQueryData([API_CACHE_KEYS.QUOTES, quoteId], quote);
      queryClient.invalidateQueries(API_CACHE_KEYS.QUOTES);
    } }
  );
};

export const useVoidQuote = (quoteId: string) => useQuoteStateMutation(quoteId, ApiRoutes.voidQuote);
export const useUnvoidQuote = (quoteId: string) => useQuoteStateMutation(quoteId, ApiRoutes.unvoidQuote);

export const downloadQuoteCsv = async (quoteId: string) => {
  const response = await axios.get(resolveDynamicUrl(ApiRoutes.downloadQuoteCsv, { quoteId }), { responseType: "blob" });
  const url = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = `CSP-CP-${quoteId}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const getQuotePdf = async (quoteId: string, mode: "view" | "download") => {
  const response = await axios.get(resolveDynamicUrl(ApiRoutes.downloadQuotePdf, { quoteId }), {
    params: mode === "view" ? { mode: "view" } : undefined,
    responseType: "blob",
  });
  const url = URL.createObjectURL(response.data);
  if (mode === "view") window.open(url, "_blank", "noopener,noreferrer");
  else {
    const link = document.createElement("a");
    link.href = url; link.download = `CSP-CP-${quoteId}.pdf`;
    document.body.appendChild(link); link.click(); link.remove();
  }
  window.setTimeout(() => URL.revokeObjectURL(url), mode === "view" ? 60000 : 0);
};

// Issue 31: the internal Affiliate Commission Report - only offered for a quote with the
// Has Affiliate toggle on; the API refuses it otherwise.
export const downloadQuoteCommissionCsv = async (quoteId: string) => {
  const response = await axios.get(resolveDynamicUrl(ApiRoutes.downloadQuoteCommissionCsv, { quoteId }), { responseType: "blob" });
  const url = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = `CSP-CP-${quoteId}-Commission.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const getQuoteCommissionPdf = async (quoteId: string, mode: "view" | "download") => {
  const response = await axios.get(resolveDynamicUrl(ApiRoutes.downloadQuoteCommissionPdf, { quoteId }), {
    params: mode === "view" ? { mode: "view" } : undefined,
    responseType: "blob",
  });
  const url = URL.createObjectURL(response.data);
  if (mode === "view") window.open(url, "_blank", "noopener,noreferrer");
  else {
    const link = document.createElement("a");
    link.href = url; link.download = `CSP-CP-${quoteId}-Commission.pdf`;
    document.body.appendChild(link); link.click(); link.remove();
  }
  window.setTimeout(() => URL.revokeObjectURL(url), mode === "view" ? 60000 : 0);
};

// No cap on failureCount - keep retrying as long as the error itself looks transient (a real,
// non-retryable failure - a 400, an auth error, etc. - still stops on the first try). The UI no
// longer shows an attempt count, so there's no reason to also stop retrying just because a
// display number would have run out.
export const shouldRetryQuoteSubscriptionImport = (_failureCount: number, error: unknown) => {
  if (!axios.isAxiosError(error)) return false;
  if (!error.response) return true;
  const status = error.response.status;
  return status === 408 || status === 429 || status >= 500;
};

// Exponential backoff (1s, 2s, 4s, ...) capped at 30s - unlike the old fixed +1s-per-attempt
// delay, this stays reasonable even if retries run for a while against a still-transient error.
export const quoteSubscriptionImportRetryDelay = (failureIndex: number) => Math.min(1000 * 2 ** failureIndex, 30000);

export const quoteSubscriptionImportError = (error: unknown) => {
  const responseData = axios.isAxiosError(error) ? error.response?.data : null;
  const detail = typeof responseData === "string" ? responseData : responseData
    ? JSON.stringify(responseData, null, 2) : error instanceof Error ? error.message : String(error);
  return `Import subscriptions failed at ${new Date().toISOString()}\n${detail}`;
};

export const useImportQuoteSubscriptions = () =>
  useMutation<IQuoteLineItem[], unknown, string>(
    async (customerGuid) => (await axios.post(resolveDynamicUrl(ApiRoutes.importQuoteSubscriptions, { customerGuid }))).data,
    { retry: shouldRetryQuoteSubscriptionImport, retryDelay: quoteSubscriptionImportRetryDelay }
  );

// A successful import that finds nothing looks identical to one that silently did nothing - the
// button just stops spinning, no new lines appear. This gives the caller something to show
// (a toast) instead of leaving that ambiguous.
export const noSubscriptionsImportedMessage = (imported: IQuoteLineItem[]) =>
  imported.length === 0 ? "No subscriptions were found to import for this customer." : null;

export const usePreviewQuotePrices = () =>
  useMutation<IQuoteLineItem[], unknown, QuotePricePreviewRequest>(
    async (request) => (await axios.post(ApiRoutes.previewQuotePrices, request)).data
  );

// The editor's live Totals table (2026-10-05): the customer PDF's Totals rows for one section,
// computed server-side by the same code as the documents. Only lines with a price are sent (the
// caller counts the rest); keyed on exactly those priced fields, so it refetches only when a price
// changes, and keepPreviousData holds the last figures on screen while the next ones load.
// `enabled` is false while the editor is repricing, so half-repriced lines are never totalled.
export const isTotalledLine = (item: IQuoteLineItem) =>
  Boolean(item.term_duration) && item.quantity >= 1 && item.total_price != null;

const totalsLine = (item: IQuoteLineItem): QuoteTotalsLine => ({
  term_duration: item.term_duration, quantity: item.quantity, total_price: item.total_price,
  sale_unit_price: item.sale_unit_price ?? null, cost_unit_price: item.cost_unit_price ?? null,
  is_commission_eligible: item.is_commission_eligible ?? null, charge_start_date: item.charge_start_date ?? null,
  charge_end_date: item.charge_end_date ?? null, billable_days: item.billable_days ?? null,
  full_period_days: item.full_period_days ?? null, proration_detail: item.proration_detail ?? null,
});

export const useQuoteTotals = (items: IQuoteLineItem[], enabled: boolean) => {
  const lines = items.filter(isTotalledLine).map(totalsLine);
  return useQuery<QuoteTotalsSection, unknown, QuoteTotalsSection>(
    [API_CACHE_KEYS.QUOTE_TOTALS, lines],
    async () => {
      const request: QuoteTotalsRequest = { sections: [{ line_items: lines }] };
      return ((await axios.post(ApiRoutes.previewQuoteTotals, request)).data as QuoteTotalsResult).sections[0];
    },
    { enabled: enabled && lines.length > 0, keepPreviousData: true, retry: 0, staleTime: Infinity }
  );
};

// Issue 35: the background promotion check. Fired (debounced) by QuoteLineItemGrid when a
// quote is opened / started and when a line's product / SKU / term / segment / quantity settle.
// It refreshes the promotions store server-side, then returns per line (in request order) the
// Microsoft percent (the pass-through cap), the promotion identity / end date / seat band, and
// an advisory eligibility state. Never blocks a quote.
export const useQuoteLinePromotions = () =>
  useMutation<QuotePromotionsResult, unknown, QuotePromotionsRequest>(
    async (request) => (await axios.post(ApiRoutes.quotePromotions, request)).data
  );

// Issue 48: the Current Promotions Viewer, offline-first. Reads the store directly - it never
// triggers a Partner Center call, so opening the dialog never blocks on the network. keepPreviousData
// keeps the table showing the prior page/filter results while a new query is in flight (no
// flash-to-empty on a filter or page change).
export const useQuotePromotionCatalog = (query: PromotionCatalogQuery, enabled = true) =>
  useQuery<PromotionCatalogResult, unknown, PromotionCatalogResult>(
    [API_CACHE_KEYS.QUOTES, "promotionsCatalog", query],
    async () => (await axios.get(ApiRoutes.quotePromotionsCatalog, { params: query })).data,
    // Unlike the rest of this file's quote queries (retry: 0 - a failure there is a real, actionable
    // error), a first-open failure here is much more likely a one-off cold-start/transient blip -
    // this never calls Partner Center itself, just reads the store. Retry (the app-wide default)
    // rather than stranding the dialog in a permanent error state the user could only work around
    // by clicking the unrelated manual Refresh button.
    { enabled, keepPreviousData: true }
  );

// Issue 48: the viewer's manual Refresh - force-syncs with Partner Center then returns the same
// shape as the catalog GET for the same filters. On success the fetched result replaces the
// matching query's cached data directly (no refetch round-trip); on failure (a network/transport
// error reaching our own API, not Partner Center - that failure comes back as a normal 200 with
// sync_ok:false) the cache, and so the visible list, is left exactly as it was.
export const useRefreshPromotionCatalog = () => {
  const queryClient = useQueryClient();
  return useMutation<PromotionCatalogResult, unknown, PromotionCatalogQuery>(
    async (query) => (await axios.post(ApiRoutes.quotePromotionsRefresh, null, { params: query })).data,
    { onSuccess: (data, query) => { queryClient.setQueryData([API_CACHE_KEYS.QUOTES, "promotionsCatalog", query], data); } }
  );
};

export const useQuoteEmailProfile = (enabled = true) => useQuery<QuoteEmailProfile, unknown, QuoteEmailProfile>(
  [API_CACHE_KEYS.QUOTES, "email-profile"], async () => (await axios.get(ApiRoutes.quoteEmailProfile)).data,
  { enabled, retry: 0 }
);

export const saveQuoteEmailSignature = async (signature: string) =>
  (await axios.put(ApiRoutes.quoteEmailProfile, { signature })).data as QuoteEmailProfile;

export const useSendQuoteEmail = (quoteId: string) => {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, QuoteEmailRequest>(
    async request => { await axios.post(resolveDynamicUrl(ApiRoutes.sendQuoteEmail, { quoteId }), request); },
    { onSuccess: () => queryClient.invalidateQueries(API_CACHE_KEYS.QUOTES) }
  );
};
